import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import { createBlog, updateBlog, getBlogById } from '../../../services/admin/blogService';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { toast } from 'react-toastify';

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const isEdit = Boolean(id);
  const editor = useRef(null);
  const fileInputRef = useRef(null);

  // Simplified form state to match API requirements
  const [formData, setFormData] = useState({
    title: '',
    description: '', // This is the short description
    fullDescription: '',
    metaTags: {
      title: '',
      description: '',
      keywords: []
    },
    featuredImage: null,
  });

  const [imagePreview, setImagePreview] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Jodit Editor configuration
  const config = useMemo(() => ({
    readonly: false,
    height: 400,
    toolbarAdaptive: false,
    toolbarSticky: true,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', '|',
      'align', '|',
      'link', 'image', '|',
      'symbol', 'table', '|',
      'undo', 'redo', '|',
      'find', 'selectall', '|',
      'preview', 'print'
    ],
    showPlaceholder: false,
  }), []);

  // Load blog data for editing
  useEffect(() => {
    if (isEdit) {
      loadBlogData();
    }
  }, [id]);

  const loadBlogData = async () => {
    try {
      setLoading(true);
      const response = await getBlogById(id);
      const blog = response.data;
      
      // Map API data to form state
      setFormData({
        title: blog.title || '',
        description: blog.description || '',
        fullDescription: blog.fullDescription || '',
        metaTags: blog.metaTags || { title: '', description: '', keywords: [] },
        //featuredImage: blog.featuredImage ? blog.featuredImage : null,
      });

      // Set image preview if image exists
      if (blog.featuredImage) {
        setImagePreview(blog.featuredImage);
        console.log('Loaded featured image:', blog.featuredImage);
      }
    } catch (error) {
      console.error('Error loading blog:', error);
      setError('Failed to load blog data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMetaTagChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      metaTags: {
        ...prev.metaTags,
        [name]: value
      }
    }));
  };

  const handleDescriptionChange = (newContent) => {
    setFormData(prev => ({
      ...prev,
      fullDescription: newContent
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        featuredImage: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      featuredImage: null
    }));
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.metaTags.keywords.includes(newKeyword.trim())) {
      setFormData(prev => ({
        ...prev,
        metaTags: {
          ...prev.metaTags,
          keywords: [...prev.metaTags.keywords, newKeyword.trim()]
        }
      }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      metaTags: {
        ...prev.metaTags,
        keywords: prev.metaTags.keywords.filter(keyword => keyword !== keywordToRemove)
      }
    }));
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Short description is required');
      return;
    }
    if (!formData.fullDescription.trim()) {
      setError('Full content is required');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare form data for API
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('fullDescription', formData.fullDescription);
      formDataToSend.append('metaTags', JSON.stringify(formData.metaTags));
      
      if (formData.featuredImage && typeof formData.featuredImage !== 'string') {
        formDataToSend.append('featuredImage', formData.featuredImage);
      }

      /* if (isEdit) {
        // Update existing blog
        await updateBlog(id, formDataToSend);
      } else {
        // Create new blog
        await createBlog(formDataToSend);
      } */

      const response = isEdit ? await updateBlog(id, formDataToSend) : await createBlog(formDataToSend);
      console.log('API Response:', response);
      if (response && response.success) {
        toast.success(response.message);
        navigate('/hpanel/blogs');
      } else {
        toast.error('Failed to create blog. Please try again.');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      setError(error.response?.data?.message || 'Failed to save blog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    // For draft functionality, you might want to save with a draft status
    // This would require API support for draft status
    console.log('Save draft functionality would be implemented here');
  };

  if (loading && isEdit) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading blog data...</div>
      </div>
    );
  }

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Add/Edit Blog | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {isEdit ? 'Edit Blog Post' : 'Write New Blog'}
            </h2>
            <p className="text-gray-400">
              {isEdit ? 'Update blog post information' : 'Create a new blog post'}
            </p>
          </div>
          <button
            onClick={() => navigate('/hpanel/blogs')}
            className="text-gray-400 hover:text-white flex items-center"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Blogs
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Blog Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter blog title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Short Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Brief description that will appear in blog listings"
                required
              />
            </div>
          </div>
        </div>

        {/* Featured Image Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Featured Image</h3>
          
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />
            
            {imagePreview ? (
              <div className="space-y-4">
                <div className="relative w-full max-w-md">
                  <img
                    src={imagePreview}
                    alt="Featured preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={triggerImageUpload}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Change Image
                </button>
              </div>
            ) : (
              <div 
                onClick={triggerImageUpload}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 cursor-pointer transition-colors"
              >
                <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-3"></i>
                <p className="text-gray-600 mb-2">Click to upload featured image</p>
                <p className="text-sm text-gray-500">Recommended size: 1200x630 pixels</p>
              </div>
            )}
          </div>
        </div>

        {/* Blog Content Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Blog Content</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Content *</label>
            <JoditEditor
              ref={editor}
              value={formData.fullDescription}
              config={config}
              onChange={handleDescriptionChange}
            />
          </div>
        </div>

        {/* SEO Meta Tags Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">SEO Meta Tags (Optional)</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
              <input
                type="text"
                name="title"
                value={formData.metaTags.title}
                onChange={handleMetaTagChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="SEO meta title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
              <textarea
                name="description"
                value={formData.metaTags.description}
                onChange={handleMetaTagChange}
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="SEO meta description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Keywords (Press Enter to add, or click on Add button)</label>
              
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.metaTags.keywords.map((keyword, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={handleKeywordKeyPress}
                    placeholder="Add a keyword"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addKeyword}
                    className="px-4 py-2 bg-primary-600 text-gray-700 rounded-lg hover:bg-primary-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/hpanel/blogs')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-300 hover:bg-gray-50 hover:text-gray-600"
            disabled={loading}
          >
            Cancel
          </button>
          <div className="flex space-x-3">
            {/* <button
              type="button"
              onClick={handleSaveDraft}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-300 hover:bg-gray-50 hover:text-gray-600"
              disabled={loading}
            >
              Save Draft
            </button> */}
            <button
              type="submit"
              className="btn-primary text-white font-semibold py-2 px-6 rounded-lg disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  {isEdit ? 'Updating...' : 'Publishing...'}
                </span>
              ) : (
                isEdit ? 'Update Blog' : 'Publish Blog'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;